import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { surveyService } from '../../services/surveyService';
import { surveysAPI } from '../../services/api';
import { 
  ArrowLeftIcon,
  ChevronDownIcon,
  XMarkIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  QrCodeIcon,
  LinkIcon,
  EyeIcon,
  DocumentDuplicateIcon,
  ArrowDownTrayIcon,
  ArrowTopRightOnSquareIcon,
  PlayIcon,
  ArrowPathIcon,
  ClockIcon,
  UserGroupIcon,
  BuildingOfficeIcon
} from '@heroicons/react/24/outline';

const SurveyLinkGenerator = () => {
  const { surveyId } = useParams();
  const navigate = useNavigate();
  const [surveyDetails, setSurveyDetails] = useState(null);
  const [surveyLinks, setSurveyLinks] = useState([]);
  const [qrCode, setQrCode] = useState('');
  const [copied, setCopied] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [readiness, setReadiness] = useState(null);
  const [surveys, setSurveys] = useState([]);
  const [selectedSurveyId, setSelectedSurveyId] = useState(surveyId || '');
  const [surveyDropdownOpen, setSurveyDropdownOpen] = useState(false);
  const [regenerating, setRegenerating] = useState('');
  const [showQrModal, setShowQrModal] = useState(false);
  const [selectedQrLink, setSelectedQrLink] = useState(null);

  const dropdownRef = useRef(null);

  // Load surveys and set initial selected survey
  useEffect(() => {
    loadSurveys();
  }, []);

  // Load survey links when selectedSurveyId changes
  useEffect(() => {
    if (selectedSurveyId) {
      loadSurveyLinks();
      checkSurveyReadiness();
      
      // Update URL if it's different from current selection
      if (selectedSurveyId !== surveyId) {
        navigate(`/survey-links/${selectedSurveyId}`, { replace: true });
      }
    }
  }, [selectedSurveyId, surveyId, navigate]);

  // Set selected survey from URL parameter when surveys are loaded
  useEffect(() => {
    if (surveys.length > 0 && surveyId && !selectedSurveyId) {
      setSelectedSurveyId(surveyId);
    }
  }, [surveys, surveyId, selectedSurveyId]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setSurveyDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const loadSurveys = async () => {
    try {
      const response = await surveysAPI.getAll();
      setSurveys(response.data);
    } catch (err) {
      console.error('Error loading surveys:', err);
      setError('Failed to load surveys');
    }
  };

  const loadSurveyLinks = async () => {
    if (!selectedSurveyId) return;
    
    try {
      setLoading(true);
      const linksData = await surveyService.getSurveyLinks(selectedSurveyId);
      
      // Transform link names to professional employee-focused names
      const transformedLinks = (linksData.links || []).map(link => {
        switch (link.type) {
          case 'Private Link':
            return { ...link, type: 'Employee Survey Link', icon: UserGroupIcon };
          case 'Public Link':
            return { ...link, type: 'Team Distribution Link', icon: BuildingOfficeIcon };
          case 'Preview Link':
            return { ...link, icon: EyeIcon };
          case 'Embed Code':
            return { ...link, icon: DocumentDuplicateIcon };
          default:
            return link;
        }
      });
      
      setSurveyLinks(transformedLinks);
      setSurveyDetails({
        SurveyName: linksData.surveyName,
        SurveyId: linksData.surveyId
      });
    } catch (err) {
      console.error('Error loading survey links:', err);
      setError('Failed to load survey links');
    } finally {
      setLoading(false);
    }
  };

  const checkSurveyReadiness = async () => {
    if (!selectedSurveyId) return;
    
    try {
      const readinessCheck = await surveyService.isSurveyReady(selectedSurveyId);
      setReadiness(readinessCheck);
    } catch (err) {
      console.error('Readiness check failed:', err);
      // Set default readiness state on error
      setReadiness({
        ready: false,
        canDistribute: false,
        validation: { isValid: false, message: 'Unable to check readiness' },
        statistics: { totalCategories: 0, totalQuestions: 0, estimatedTime: 0 },
        recommendations: ['Check survey configuration']
      });
    }
  };

  const handleSurveyChange = (survey) => {
    setSelectedSurveyId(survey.id);
    setSurveyDropdownOpen(false);
    setSurveyLinks([]);
    setQrCode('');
    setError('');
    setReadiness(null);
  };

  const handleCopy = async (text, type) => {
    try {
      const result = await surveyService.copyToClipboard(text);
      if (result.success) {
        setCopied(type);
        setTimeout(() => setCopied(''), 2000);
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError('Failed to copy to clipboard');
    }
  };

  const handleOpenLink = (link) => {
    if (!selectedSurveyId) {
      setError('No survey selected');
      return;
    }
    
    try {
      console.log('🔗 Opening link type:', link.type, 'for survey:', selectedSurveyId);
      
      // Route to different components based on link type
      switch (link.type) {
        case 'Employee Survey Link':
          console.log('👥 Opening Employee Survey Link in new tab');
          if (link.url) {
            let finalUrl = link.url;
            
            // Ensure URL has proper protocol
            if (!finalUrl.startsWith('http://') && !finalUrl.startsWith('https://')) {
              finalUrl = `https://${finalUrl}`;
            }
            
            console.log('🔗 Opening Employee Survey URL:', finalUrl);
            window.open(finalUrl, '_blank', 'noopener,noreferrer');
          } else {
            setError('No URL available for Employee Survey');
          }
          break;
          
        case 'Team Distribution Link':
          console.log('🏢 Opening Team Distribution Link in new tab');
          if (link.url) {
            let finalUrl = link.url;
            
            // Ensure URL has proper protocol
            if (!finalUrl.startsWith('http://') && !finalUrl.startsWith('https://')) {
              finalUrl = `https://${finalUrl}`;
            }
            
            console.log('🔗 Opening Team Distribution URL:', finalUrl);
            window.open(finalUrl, '_blank', 'noopener,noreferrer');
          } else {
            setError('No URL available for Team Distribution');
          }
          break;
          
        case 'Preview Link':
          console.log('👀 Navigating to Survey Preview');
          navigate(`/survey-preview/${selectedSurveyId}`);
          break;
          
        default:
          // For other link types or direct URLs, open in new tab
          if (link.url) {
            let finalUrl = link.url;
            
            // Ensure URL has proper protocol
            if (!finalUrl.startsWith('http://') && !finalUrl.startsWith('https://')) {
              finalUrl = `https://${finalUrl}`;
            }
            
            console.log('🔗 Opening external URL:', finalUrl);
            window.open(finalUrl, '_blank', 'noopener,noreferrer');
          } else {
            setError('No URL available to open');
          }
      }
    } catch (err) {
      console.error('❌ Error opening link:', err);
      setError('Failed to open link: ' + err.message);
    }
  };

  const handleGenerateQR = async (link) => {
    try {
      setLoading(true);
      // Convert link type to match backend expectations - use original types for API calls
      const linkType = getOriginalLinkType(link.type);
      const qrData = await surveyService.getQRCodeAsBase64(selectedSurveyId, linkType);
      setQrCode(qrData);
      setSelectedQrLink(link);
      setShowQrModal(true);
    } catch (err) {
      console.error('Error generating QR code:', err);
      setError('Failed to generate QR code: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get original link type for API calls
  const getOriginalLinkType = (displayType) => {
    switch (displayType) {
      case 'Employee Survey Link':
        return 'private-link';
      case 'Team Distribution Link':
        return 'public-link';
      case 'Preview Link':
        return 'preview-link';
      default:
        return displayType.toLowerCase().replace(' ', '-');
    }
  };

  const downloadQRCode = async () => {
    try {
      await surveyService.downloadQRCode(selectedSurveyId, `survey-${selectedSurveyId}-qrcode.png`);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleRegenerate = async (linkType) => {
    try {
      setRegenerating(linkType);
      let response;
      
      console.log('🔄 Regenerating link type:', linkType);
      
      // Convert display type back to original type for API calls
      const originalType = getOriginalLinkType(linkType);
      
      if (originalType === 'private-link') {
        response = await surveyService.regeneratePrivateLink(selectedSurveyId);
      } else if (originalType === 'public-link') {
        response = await surveyService.regeneratePublicLink(selectedSurveyId);
      } else {
        throw new Error(`Unsupported link type: ${linkType}`);
      }

      if (response) {
        // Update the links list with the new URL
        setSurveyLinks(prevLinks => 
          prevLinks.map(link => 
            link.type === linkType 
              ? { ...link, url: response.url, expiration: response.expiration }
              : link
          )
        );
        
        // Show success message
        setCopied(`${linkType.toLowerCase().replace(' ', '-')}-regenerated`);
        setTimeout(() => setCopied(''), 2000);
        
        console.log('✅ Link regenerated successfully:', linkType);
      }
    } catch (err) {
      console.error('❌ Error regenerating link:', err);
      setError(`Failed to regenerate ${linkType}: ${err.message}`);
    } finally {
      setRegenerating('');
    }
  };

  const handlePreview = () => {
    if (selectedSurveyId) {
      console.log('👀 Navigating to preview for survey:', selectedSurveyId);
      navigate(`/survey-preview/${selectedSurveyId}`);
    } else {
      setError('Please select a survey first');
    }
  };

  const navigateToPreview = () => {
    if (selectedSurveyId) {
      console.log('👀 Navigating to preview for survey:', selectedSurveyId);
      navigate(`/survey-preview/${selectedSurveyId}`);
    } else {
      setError('Please select a survey first');
    }
  };

  const getSelectedSurvey = () => {
    return surveys.find(s => s.id === parseInt(selectedSurveyId));
  };

  const getLinkActions = (link) => {
    const actions = [];
    const LinkIcon = link.icon; // Use the assigned icon for each link type
    
    // Add actions based on link type
    switch (link.type) {
      case 'Preview Link':
        actions.push(
          {
            label: 'Open Preview',
            icon: ArrowTopRightOnSquareIcon,
            onClick: () => handleOpenLink(link),
            variant: 'primary'
          },
          {
            label: 'Test Survey',
            icon: PlayIcon,
            onClick: handlePreview,
            variant: 'primary'
          }
        );
        break;
        
      case 'Employee Survey Link':
        actions.push(
          {
            label: copied === link.type ? 'Copied!' : 'Copy Link',
            icon: DocumentDuplicateIcon,
            onClick: () => handleCopy(link.url, link.type),
            variant: copied === link.type ? 'success' : 'primary'
          },
          {
            label: 'Open Survey',
            icon: ArrowTopRightOnSquareIcon,
            onClick: () => handleOpenLink(link),
            variant: 'primary'
          },
          {
            label: 'QR Code',
            icon: QrCodeIcon,
            onClick: () => handleGenerateQR(link),
            variant: 'primary'
          },
          {
            label: regenerating === link.type ? 'Regenerating...' : 'New Link',
            icon: ArrowPathIcon,
            onClick: () => handleRegenerate(link.type),
            variant: 'warning',
            disabled: regenerating === link.type
          }
        );
        break;
        
      case 'Team Distribution Link':
        actions.push(
          {
            label: copied === link.type ? 'Copied!' : 'Copy Link',
            icon: DocumentDuplicateIcon,
            onClick: () => handleCopy(link.url, link.type),
            variant: copied === link.type ? 'success' : 'primary'
          },
          {
            label: 'Open Survey',
            icon: ArrowTopRightOnSquareIcon,
            onClick: () => handleOpenLink(link),
            variant: 'primary'
          },
          {
            label: 'QR Code',
            icon: QrCodeIcon,
            onClick: () => handleGenerateQR(link),
            variant: 'primary'
          },
          {
            label: regenerating === link.type ? 'Regenerating...' : 'New Link',
            icon: ArrowPathIcon,
            onClick: () => handleRegenerate(link.type),
            variant: 'warning',
            disabled: regenerating === link.type
          }
        );
        break;
        
      case 'Embed Code':
        actions.push(
          {
            label: copied === link.type ? 'Copied!' : 'Copy Code',
            icon: DocumentDuplicateIcon,
            onClick: () => handleCopy(link.code, link.type),
            variant: copied === link.type ? 'success' : 'primary'
          }
        );
        break;
        
      default:
        // Default actions for unknown link types
        if (link.url) {
          actions.push(
            {
              label: 'Open',
              icon: ArrowTopRightOnSquareIcon,
              onClick: () => handleOpenLink(link),
              variant: 'primary'
            },
            {
              label: copied === link.type ? 'Copied!' : 'Copy',
              icon: DocumentDuplicateIcon,
              onClick: () => handleCopy(link.url, link.type),
              variant: copied === link.type ? 'success' : 'primary'
            }
          );
        }
    }
    
    return actions;
  };

  const getLinkDescription = (linkType) => {
    switch (linkType) {
      case 'Employee Survey Link':
        return 'Secure link for employee surveys and feedback collection';
      case 'Team Distribution Link':
        return 'General distribution link for team-wide surveys';
      case 'Preview Link':
        return 'Preview and test the survey before distribution';
      case 'Embed Code':
        return 'HTML code for embedding survey in websites or portals';
      default:
        return 'Survey distribution link';
    }
  };

  if (!surveys.length) {
    return (
      <div className="p-6 w-full bg-gradient-to-br from-blue-50 via-white to-blue-50 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-900">Loading Surveys...</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 w-full bg-gradient-to-br from-blue-50 via-white to-blue-50 min-h-screen">
      {/* Header with Survey Selector */}
      <div className="bg-white rounded-2xl shadow-sm border border-blue-100 p-6 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/surveys')}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              title="Back to Surveys"
            >
              <ArrowLeftIcon className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Survey Distribution
              </h1>
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-gray-600">Survey:</span>
                <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setSurveyDropdownOpen(!surveyDropdownOpen)}
                    className="flex items-center gap-2 px-3 py-2 bg-blue-50 text-blue-700 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
                  >
                    <span className="font-medium">
                      {getSelectedSurvey() ? getSelectedSurvey().name : 'Select Survey'}
                    </span>
                    <ChevronDownIcon className={`w-4 h-4 transition-transform ${surveyDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {surveyDropdownOpen && (
                    <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 min-w-64 max-h-60 overflow-y-auto">
                      {surveys.map((survey) => (
                        <button
                          key={survey.id}
                          onClick={() => handleSurveyChange(survey)}
                          className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors ${
                            selectedSurveyId === survey.id 
                              ? 'bg-blue-50 text-blue-700 font-medium border-l-4 border-blue-500' 
                              : 'text-gray-700 border-l-4 border-transparent'
                          }`}
                        >
                          <div className="font-medium text-sm">{survey.name}</div>
                          <div className="text-xs text-gray-500 mt-1">
                            {survey.description || 'No description available'}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {surveyDetails && (
          <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <h3 className="font-semibold text-gray-900 text-lg">{surveyDetails.SurveyName}</h3>
            <p className="text-gray-600 text-sm mt-1">Survey ID: {surveyDetails.SurveyId}</p>
          </div>
        )}
      </div>

      {/* Survey Not Selected State */}
      {!selectedSurveyId && (
        <div className="bg-white rounded-2xl shadow-sm border border-blue-100 p-8 text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full flex items-center justify-center mx-auto mb-4">
            <LinkIcon className="w-8 h-8 text-blue-500" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Survey Selected</h3>
          <p className="text-gray-500 mb-6">
            Please select a survey from the dropdown above to generate distribution links.
          </p>
          <div className="flex justify-center">
            <div className="bg-blue-50 px-4 py-3 rounded-lg border border-blue-200">
              <span className="text-2xl font-bold text-blue-600">{surveys.length}</span>
              <span className="text-sm text-blue-700 ml-2">Available Surveys</span>
            </div>
          </div>
        </div>
      )}

      {/* Survey Content - Only show when survey is selected */}
      {selectedSurveyId && (
        <>
          {/* Error Display */}
          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-2xl p-4">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
                <div className="ml-auto pl-3">
                  <button 
                    onClick={() => setError('')}
                    className="text-red-400 hover:text-red-600"
                  >
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Readiness Check */}
          {readiness && (
            <div className={`bg-white rounded-2xl shadow-sm border border-blue-100 p-6 mb-6 ${
              readiness.ready ? 'border-l-4 border-l-green-500' : 'border-l-4 border-l-amber-500'
            }`}>
              <div className="flex items-center gap-3 mb-4">
                <div className={`p-2 rounded-full ${
                  readiness.ready ? 'bg-green-100 text-green-600' : 'bg-amber-100 text-amber-600'
                }`}>
                  {readiness.ready ? (
                    <CheckCircleIcon className="w-5 h-5" />
                  ) : (
                    <ExclamationTriangleIcon className="w-5 h-5" />
                  )}
                </div>
                <h4 className="text-lg font-semibold text-gray-900">Survey Readiness</h4>
              </div>
              <p className="text-gray-600 mb-4">{readiness.validation?.message || 'Survey is ready for distribution'}</p>
              
              {readiness.statistics && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 text-center">
                    <div className="text-2xl font-bold text-blue-600">{readiness.statistics.totalCategories || 0}</div>
                    <div className="text-sm text-gray-600">Categories</div>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 text-center">
                    <div className="text-2xl font-bold text-blue-600">{readiness.statistics.totalQuestions || 0}</div>
                    <div className="text-sm text-gray-600">Questions</div>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 text-center">
                    <div className="text-2xl font-bold text-blue-600">{readiness.statistics.estimatedTime || 0}m</div>
                    <div className="text-sm text-gray-600">Est. Time</div>
                  </div>
                </div>
              )}

              {readiness.recommendations && readiness.recommendations.length > 0 && (
                <div className="border-t border-gray-200 pt-4">
                  <h5 className="font-medium text-gray-900 mb-3">Recommendations:</h5>
                  <ul className="space-y-2">
                    {readiness.recommendations.map((rec, index) => (
                      <li key={index} className="flex items-start gap-2 text-sm text-gray-600">
                        <div className="w-1.5 h-1.5 bg-amber-500 rounded-full mt-1.5 flex-shrink-0"></div>
                        {rec}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="bg-white rounded-2xl shadow-sm border border-blue-100 p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
              <h4 className="text-lg font-semibold text-gray-900 mb-2">Loading Survey Links</h4>
              <p className="text-gray-600">Please wait while we load your distribution links...</p>
            </div>
          )}

          {/* Survey Links Display */}
          {!loading && surveyLinks.length > 0 && (
            <div className="space-y-6">
              {/* Links Grid */}
              <div className="grid gap-6">
                {surveyLinks.map((link, index) => {
                  const actions = getLinkActions(link);
                  const LinkIcon = link.icon;
                  
                  return (
                    <div key={index} className="bg-white rounded-2xl shadow-sm border border-blue-100 p-6 hover:shadow-md transition-shadow">
                      <div className="flex items-start gap-4 mb-4">
                        <div className="flex-shrink-0">
                          <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                            <LinkIcon className="w-6 h-6 text-blue-600" />
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <h3 className="text-lg font-semibold text-gray-900">{link.type}</h3>
                              <p className="text-sm text-gray-500 mt-1">
                                {getLinkDescription(link.type)}
                              </p>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-gray-500">
                              <ClockIcon className="w-4 h-4" />
                              <span>Expires: {link.expiration || 'Never'}</span>
                            </div>
                          </div>
                          
                          {/* URL or Code Display */}
                          <div className="bg-gray-50 rounded-lg p-4 mb-4">
                            {link.type === 'Embed Code' ? (
                              <div>
                                <p className="text-sm font-medium text-gray-700 mb-2">Embed Code:</p>
                                <code className="text-sm bg-white p-3 rounded border block overflow-x-auto font-mono">
                                  {link.code}
                                </code>
                              </div>
                            ) : (
                              <div>
                                <p className="text-sm font-medium text-gray-700 mb-2">Survey URL:</p>
                                <p className="text-sm text-blue-600 break-all bg-white p-3 rounded border font-mono">
                                  {link.url}
                                </p>
                              </div>
                            )}
                          </div>

                          {/* Action Buttons */}
                          <div className="flex flex-wrap gap-2">
                            {actions.map((action, actionIndex) => (
                              <button
                                key={actionIndex}
                                onClick={action.onClick}
                                disabled={action.disabled}
                                className={`inline-flex items-center px-3 py-2 border text-sm font-medium rounded-md transition-colors ${
                                  action.variant === 'success' 
                                    ? 'bg-green-50 border-green-200 text-green-700 hover:bg-green-100' 
                                    : action.variant === 'warning'
                                      ? 'bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100'
                                      : 'bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100'
                                } ${action.disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                              >
                                {action.disabled ? (
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-amber-500 mr-2"></div>
                                ) : (
                                  <action.icon className="w-4 h-4 mr-2" />
                                )}
                                {action.label}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-4 justify-center pt-6">
                <button 
                  onClick={navigateToPreview}
                  className="px-6 py-3 bg-amber-500 text-white rounded-lg font-medium hover:bg-amber-600 transition-colors flex items-center gap-2"
                >
                  <EyeIcon className="w-4 h-4" />
                  Preview Survey
                </button>
                <button 
                  onClick={() => {
                    const employeeLink = surveyLinks.find(link => link.type === 'Employee Survey Link');
                    if (employeeLink && employeeLink.url) {
                      let finalUrl = employeeLink.url;
                      if (!finalUrl.startsWith('http://') && !finalUrl.startsWith('https://')) {
                        finalUrl = `https://${finalUrl}`;
                      }
                      window.open(finalUrl, '_blank', 'noopener,noreferrer');
                    } else {
                      setError('Employee Survey Link not available');
                    }
                  }}
                  className="px-6 py-3 bg-green-500 text-white rounded-lg font-medium hover:bg-green-600 transition-colors flex items-center gap-2"
                >
                  <UserGroupIcon className="w-4 h-4" />
                  Open Employee Survey
                </button>
                <button 
                  onClick={() => {
                    const teamLink = surveyLinks.find(link => link.type === 'Team Distribution Link');
                    if (teamLink && teamLink.url) {
                      let finalUrl = teamLink.url;
                      if (!finalUrl.startsWith('http://') && !finalUrl.startsWith('https://')) {
                        finalUrl = `https://${finalUrl}`;
                      }
                      window.open(finalUrl, '_blank', 'noopener,noreferrer');
                    } else {
                      setError('Team Distribution Link not available');
                    }
                  }}
                  className="px-6 py-3 bg-blue-500 text-white rounded-lg font-medium hover:bg-blue-600 transition-colors flex items-center gap-2"
                >
                  <BuildingOfficeIcon className="w-4 h-4" />
                  Open Team Survey
                </button>
                <button 
                  className="px-6 py-3 bg-purple-500 text-white rounded-lg font-medium hover:bg-purple-600 transition-colors flex items-center gap-2"
                  onClick={loadSurveyLinks}
                >
                  <ArrowPathIcon className="w-4 h-4" />
                  Refresh Links
                </button>
              </div>
            </div>
          )}
          <br />
          {/* No Links State */}
          {!loading && selectedSurveyId && surveyLinks.length === 0 && (
            <div className="bg-white rounded-2xl shadow-sm border border-blue-100 p-8 text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full flex items-center justify-center mx-auto mb-4">
                <LinkIcon className="w-8 h-8 text-blue-500" />
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-2">No Links Generated</h4>
              <p className="text-gray-600 mb-6 max-w-md mx-auto">
                Survey links will be automatically generated when you select a survey. If you're not seeing any links, try refreshing the page.
              </p>
              <button 
                className="px-6 py-3 bg-blue-500 text-white rounded-lg font-medium hover:bg-blue-600 transition-colors flex items-center gap-2 mx-auto"
                onClick={loadSurveyLinks}
              >
                <ArrowPathIcon className="w-4 h-4" />
                Load Links
              </button>
            </div>
          )}

          {/* Distribution Tips */}
          <div className="bg-white rounded-2xl shadow-sm border border-blue-100 p-6 mb-6">
            <h4 className="text-xl font-semibold text-gray-900 mb-6 text-center">Distribution Best Practices</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-gray-50 p-5 rounded-lg border border-gray-200 text-center hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <UserGroupIcon className="w-6 h-6 text-blue-600" />
                </div>
                <h5 className="font-semibold text-gray-900 mb-2">Employee Surveys</h5>
                <p className="text-sm text-gray-600">Use Employee Survey Link for confidential feedback</p>
              </div>
              <div className="bg-gray-50 p-5 rounded-lg border border-gray-200 text-center hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <BuildingOfficeIcon className="w-6 h-6 text-green-600" />
                </div>
                <h5 className="font-semibold text-gray-900 mb-2">Team Surveys</h5>
                <p className="text-sm text-gray-600">Use Team Distribution Link for department-wide surveys</p>
              </div>
              <div className="bg-gray-50 p-5 rounded-lg border border-gray-200 text-center hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <QrCodeIcon className="w-6 h-6 text-purple-600" />
                </div>
                <h5 className="font-semibold text-gray-900 mb-2">QR Codes</h5>
                <p className="text-sm text-gray-600">Generate QR codes for easy mobile access</p>
              </div>
              <div className="bg-gray-50 p-5 rounded-lg border border-gray-200 text-center hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <ClockIcon className="w-6 h-6 text-amber-600" />
                </div>
                <h5 className="font-semibold text-gray-900 mb-2">Optimal Timing</h5>
                <p className="text-sm text-gray-600">Send surveys Tuesday-Thursday for better response rates</p>
              </div>
            </div>
          </div>
        </>
      )}

      {/* QR Code Modal */}
      {showQrModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full mx-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {selectedQrLink?.type} QR Code
              </h3>
              <button
                onClick={() => setShowQrModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
            <div className="flex justify-center mb-4">
              <img 
                src={qrCode} 
                alt="QR Code" 
                className="w-64 h-64 rounded-lg border border-gray-300"
              />
            </div>
            <div className="text-center mb-4">
              <p className="text-sm text-gray-600">
                Scan this QR code to access the survey on mobile devices
              </p>
            </div>
            <div className="flex justify-center gap-3">
              <button
                onClick={downloadQRCode}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-2"
              >
                <ArrowDownTrayIcon className="w-4 h-4" />
                Download QR
              </button>
              <button
                onClick={() => setShowQrModal(false)}
                className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SurveyLinkGenerator;